import React from "react";
import { useTranslation } from "react-i18next";
import { useComparisonLabels } from "../../../compare/ComparisonLabelsContext";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./simulazioneAndamentoPortafoglio.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type ComponentProps = { data: any; data2?: any; title };
const SimulazioneAndamentoPortafoglio: React.FC<ComponentProps> = ({
  data,
  data2,
  title,
}) => {
  const { t, i18n } = useTranslation();
  const labels = useComparisonLabels();
  if (!data) {
    return null;
  }
  const dataAndamento = data.analisi_backtest.simulazione_andamento_portafoglio;
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  // Build combined data using a single pass with Map
  const dateMap = new Map();

  // Add first dataset
  dataAndamento.forEach((item) => {
    dateMap.set(item.date, {
      originalDate: item.date,
      value: parseFloat(item.value),
    });
  });

  // Merge second dataset if exists
  if (data2?.analisi_backtest?.simulazione_andamento_portafoglio) {
    data2.analisi_backtest.simulazione_andamento_portafoglio.forEach((item) => {
      const existing = dateMap.get(item.date);
      if (existing) {
        existing.value2 = parseFloat(item.value);
      } else {
        dateMap.set(item.date, {
          originalDate: item.date,
          value2: parseFloat(item.value),
        });
      }
    });
  }

  // Convert to sorted array
  const combinedData = Array.from(dateMap.values()).sort(
    (a, b) => new Date(a.originalDate).getTime() - new Date(b.originalDate).getTime()
  );

  // Italian month abbreviations
  const ITALIAN_MONTHS: { [key: number]: string } = {
    0: "Gen", 1: "Feb", 2: "Mar", 3: "Apr", 4: "Mag", 5: "Giu",
    6: "Lug", 7: "Ago", 8: "Set", 9: "Ott", 10: "Nov", 11: "Dic"
  };

  // Smart axis configuration based on data timeline
  const getSmartAxisConfig = (data: typeof combinedData) => {
    if (!data.length) return { ticks: [], tickFormatter: () => "" };

    // Count unique years and months
    const uniqueYears = new Set<number>();
    const uniqueMonths = new Set<string>();

    data.forEach((point) => {
      const date = new Date(point.originalDate);
      uniqueYears.add(date.getFullYear());
      uniqueMonths.add(`${date.getFullYear()}-${date.getMonth()}`);
    });

    const numYears = uniqueYears.size;
    const numMonths = uniqueMonths.size;

    // Strategy: >= 5 years → yearly ticks, < 5 years → 7 evenly spaced ticks
    if (numYears >= 5) {
      // Find year boundary indices and dates
      const ticks: string[] = [];
      let prevYear: number | null = null;

      data.forEach((point) => {
        const year = new Date(point.originalDate).getFullYear();
        if (prevYear !== year) {
          ticks.push(point.originalDate);
          prevYear = year;
        }
      });

      return {
        ticks,
        tickFormatter: (value: string) => {
          return new Date(value).getFullYear().toString();
        }
      };
    } else {
      // < 5 years: 7 evenly spaced labels
      const includeDay = numMonths < 5;
      let tickIndices: number[];

      if (data.length <= 1) {
        tickIndices = [0];
      } else if (data.length < 7) {
        tickIndices = Array.from({ length: data.length }, (_, i) => i);
      } else {
        const step = (data.length - 1) / 6;
        tickIndices = Array.from({ length: 7 }, (_, i) => Math.round(i * step));
      }

      const ticks = tickIndices.map(i => data[i].originalDate);

      return {
        ticks,
        tickFormatter: (value: string) => {
          const date = new Date(value);
          const month = ITALIAN_MONTHS[date.getMonth()];
          const year = date.getFullYear();

          if (includeDay) {
            return `${date.getDate()} ${month} ${year}`;
          }
          return `${month} ${year}`;
        }
      };
    }
  };

  const { ticks: xAxisTicks, tickFormatter: xAxisTickFormatter } = getSmartAxisConfig(combinedData);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  const allReturns = combinedData
    .flatMap((d) => [
      d.value !== undefined && d.value !== null && d.value !== 0 ? d.value : [],
      d.value2 !== undefined && d.value2 !== null && d.value2 !== 0 ? d.value2 : [],
    ])
    .filter((val) => val !== undefined && val !== null && val !== 0 && typeof val === 'number');

  // Calculate the actual data range
  const actualMin = Math.min(...allReturns);
  const actualMax = Math.max(...allReturns);

  // Smart step sizing based on data range (matching Python backend logic)
  let step = 20;
  if (actualMin > 90 && actualMax < 110) {
    step = 5;
  }

  // Adjust min/max to step boundaries
  const adjustedMin = Math.floor(actualMin / step) * step;
  const adjustedMax = Math.ceil(actualMax / step) * step;

  // Generate tick values
  const yAxisTicks = Array.from(
    { length: Math.floor((adjustedMax - adjustedMin) / step) + 1 },
    (_, i) => adjustedMin + i * step
  );

  // Custom tooltip with background
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // Format the original date
      const originalDate = new Date(payload[0].payload.originalDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);

      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          {payload.map((entry: any, index: number) => {
            // Skip undefined values
            if (entry.value === undefined) return null;

            // Determine the portfolio name based on dataKey
            const portfolioName =
              entry.dataKey === "value" ? labels.portfolio1 : labels.portfolio2;

            return (
              <p
                key={`value-${index}`}
                style={{
                  color: entry.color,
                  margin: "3px 0",
                  fontSize: "12px",
                }}
              >
                {data2 ? portfolioName : t(`portfolio.${entry.name}`)}:{" "}
                {formatItalianNumber(entry.value)}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const CustomizedLegend = () => {
    if (!data2) return null;
    const legendItems = [
      {
        value: labels.portfolio1,
        color: colorsConfig.portfolio1,
      },
      {
        value: labels.portfolio2,
        color: colorsConfig.portfolio2,
      },
    ];

    return (
      <div className={styles.legendContainer}>
        {legendItems.map((entry, index) => (
          <div key={`item-${index}`} className={styles.legendItem}>
            <div
              className={styles.legendDot}
              style={{ backgroundColor: entry.color }}
            />
            <span className={styles.legendCopy}>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer debounce={500}>
          <LineChart
            data={combinedData}
            margin={{ top: 10, right: 40, left: -15, bottom: 10 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="originalDate"
              ticks={xAxisTicks}
              tickFormatter={xAxisTickFormatter}
              dx={10}
              dy={5}
              textAnchor="middle"
              height={40}
              tick={chartCopy}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tick={chartCopy}
              domain={[adjustedMin, adjustedMax]}
              ticks={yAxisTicks}
              tickLine={false}
              tickFormatter={(value) => Math.round(value).toString()}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomizedLegend />} />
            <ReferenceLine y={0} stroke={colorsConfig.FinecoBlack} />
            <Line
              name={data2 ? labels.portfolio1 : "value"}
              type="linear"
              dataKey="value"
              stroke={colorsConfig.portfolio1}
              dot={false}
              strokeWidth={1.5}
              strokeOpacity={1}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {data2 && (
              <Line
                name={labels.portfolio2}
                type="linear"
                dataKey="value2"
                stroke={colorsConfig.portfolio2}
                dot={false}
                strokeWidth={1.5}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SimulazioneAndamentoPortafoglio;
